/**
 * FileUpload - Componente para upload de arquivos/imagens
 * Suporta seleção de arquivo do dispositivo ou captura direta
 * Faz upload automático para o Supabase Storage
 */

import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, Check, Image, FileImage, Loader2, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface FileUploadProps {
    value?: string;
    onChange: (url: string) => void;
    userId: string;
    label?: string;
    placeholder?: string;
    description?: string;
    error?: string;
    required?: boolean;
    accept?: string;
    folder?: string;
    maxSizeMB?: number;
}

export function FileUpload({
    value,
    onChange,
    userId,
    label = 'Upload de Arquivo',
    placeholder = 'Selecione ou arraste um arquivo',
    description,
    error,
    required = false,
    accept = 'image/*',
    folder = 'driver-uploads',
    maxSizeMB = 10
}: FileUploadProps) {
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);

    const inputRef = useRef<HTMLInputElement>(null);

    // Processar arquivo selecionado
    const processFile = useCallback(async (file: File) => {
        // Validar tipo
        if (!file.type.startsWith('image/')) {
            toast.error('Por favor, selecione uma imagem válida.');
            return;
        }

        // Validar tamanho
        const sizeMB = file.size / (1024 * 1024);
        if (sizeMB > maxSizeMB) {
            toast.error(`O arquivo deve ter no máximo ${maxSizeMB}MB.`);
            return;
        }

        try {
            setIsUploading(true);

            // Verificar sessão atual do Supabase
            const { data: { session }, error: sessionError } = await supabase.auth.getSession();

            if (sessionError || !session) {
                console.error('Erro de sessão:', sessionError);
                toast.error('Sessão expirada. Faça login novamente.');
                return;
            }

            console.log('[FileUpload] Sessão válida, userId:', session.user.id);

            // Criar preview local
            const reader = new FileReader();
            reader.onload = (e) => {
                setPreviewUrl(e.target?.result as string);
            };
            reader.readAsDataURL(file);

            // Gerar nome único - usando userId na raiz (padrão do storage)
            const fileExt = file.name.split('.').pop()?.toLowerCase() || 'jpg';
            const fileName = `${folder}_${Date.now()}.${fileExt}`;
            const filePath = `${session.user.id}/${fileName}`;

            console.log('[FileUpload] Fazendo upload para:', filePath);

            // Upload para Supabase Storage
            const { data, error: uploadError } = await supabase.storage
                .from('Driver-documents')
                .upload(filePath, file, {
                    contentType: file.type,
                    cacheControl: '3600',
                    upsert: true
                });

            if (uploadError) {
                console.error('[FileUpload] Erro no upload:', uploadError);
                throw uploadError;
            }

            console.log('[FileUpload] Upload bem sucedido:', data);

            // Obter URL pública
            const { data: { publicUrl } } = supabase.storage
                .from('Driver-documents')
                .getPublicUrl(filePath);

            onChange(publicUrl);
            toast.success('Arquivo enviado com sucesso!');

        } catch (err: any) {
            console.error('Erro no upload:', err);
            toast.error(err?.message || 'Erro ao enviar arquivo. Tente novamente.');
            setPreviewUrl(null);
        } finally {
            setIsUploading(false);
        }
    }, [folder, maxSizeMB, onChange]);

    // Handler para input de arquivo
    const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            processFile(file);
        }
    }, [processFile]);

    // Handlers de drag & drop
    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        const file = e.dataTransfer.files?.[0];
        if (file) {
            processFile(file);
        }
    }, [processFile]);

    // Remover arquivo
    const handleRemove = useCallback(() => {
        onChange('');
        setPreviewUrl(null);
        if (inputRef.current) {
            inputRef.current.value = '';
        }
    }, [onChange]);

    // Abrir seletor de arquivos
    const openFilePicker = useCallback(() => {
        inputRef.current?.click();
    }, []);

    return (
        <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
                {label} {required && <span className="text-red-500">*</span>}
            </label>

            {description && (
                <p className="text-xs text-gray-500 mb-2">{description}</p>
            )}

            {/* Preview da imagem salva */}
            {(value || previewUrl) && !isUploading ? (
                <div className="relative group">
                    <div className="w-full aspect-video max-w-sm rounded-xl overflow-hidden border-2 border-emerald-200 shadow-lg bg-gray-100">
                        <img
                            src={previewUrl || value}
                            alt="Preview do arquivo"
                            className="w-full h-full object-contain"
                        />
                    </div>

                    {/* Overlay com ações */}
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center gap-3">
                        <button
                            type="button"
                            onClick={openFilePicker}
                            className="px-4 py-2 bg-white text-gray-800 text-sm font-medium rounded-lg shadow-lg hover:bg-gray-100 transition-all flex items-center gap-2"
                        >
                            <Upload className="w-4 h-4" />
                            Trocar
                        </button>
                        <button
                            type="button"
                            onClick={handleRemove}
                            className="px-4 py-2 bg-red-500 text-white text-sm font-medium rounded-lg shadow-lg hover:bg-red-600 transition-all flex items-center gap-2"
                        >
                            <Trash2 className="w-4 h-4" />
                            Remover
                        </button>
                    </div>

                    {/* Badge de sucesso */}
                    <div className="absolute top-2 right-2 px-2 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Enviado
                    </div>
                </div>
            ) : (
                /* Área de upload */
                <div
                    onClick={openFilePicker}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`w-full p-6 border-2 border-dashed rounded-2xl transition-all cursor-pointer flex flex-col items-center gap-3 ${isUploading
                        ? 'border-emerald-300 bg-emerald-50'
                        : isDragOver
                            ? 'border-emerald-400 bg-emerald-50 scale-[1.02]'
                            : error
                                ? 'border-red-300 bg-red-50 hover:border-red-400'
                                : 'border-gray-300 bg-gray-50 hover:border-emerald-400 hover:bg-emerald-50'
                        }`}
                >
                    {isUploading ? (
                        <>
                            <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
                            <div className="text-center">
                                <p className="font-medium text-emerald-600">
                                    Enviando arquivo...
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    Aguarde o upload completar
                                </p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${error ? 'bg-red-100' : 'bg-emerald-100'
                                }`}>
                                <FileImage className={`w-7 h-7 ${error ? 'text-red-500' : 'text-emerald-600'}`} />
                            </div>
                            <div className="text-center">
                                <p className={`font-medium ${error ? 'text-red-600' : 'text-gray-700'}`}>
                                    {placeholder}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">
                                    PNG, JPG ou JPEG (máx. {maxSizeMB}MB)
                                </p>
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* Input oculto */}
            <input
                ref={inputRef}
                type="file"
                accept={accept}
                onChange={handleFileChange}
                className="hidden"
            />

            {/* Mensagem de erro */}
            {error && (
                <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                    <X className="w-4 h-4" />
                    {error}
                </p>
            )}
        </div>
    );
}

export default FileUpload;
